import { CommonModule } from "@angular/common";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import Swal from "sweetalert2";

import { ApiService } from "../../../../shared/services/api/api.service";
import { API_ENDPOINTS } from "../../../../shared/services/api/api-endpoints";
import { LoaderComponent } from "../../../../shared/components/loader/loader.component";
import { environment } from "../../../../../environments/environment";

@Component({
  selector: "business-account-store",
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, LoaderComponent],
  templateUrl: "./store.component.html",
  styleUrl: "./store.component.scss",
})
export class BusinessAccountStoreComponent implements OnInit {
  assetUrl = environment.assetBaseUrl;

  @ViewChild("logoInput") logoInput!: ElementRef<HTMLInputElement>;
  @ViewChild("photosInput") photosInput!: ElementRef<HTMLInputElement>;

  storeSettingsForm: FormGroup;
  isLoading = false;

  logoPreview: string | null = null;
  photoPreviews: string[] = [];

  readonly MAX_PHOTOS = 3;
  readonly MAX_FILE_SIZE_MB = 5;

  private lastLoadedData: any = null;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
  ) {
    this.storeSettingsForm = this.fb.group({
      business_name: ["", Validators.required],
      userName: ["", Validators.required],
      description: ["", Validators.required],
      logo: [""],
      photos: this.fb.array([]),
      logo_in_repost: [false],
      logo_in_emails: [false],
    });
  }

  ngOnInit(): void {
    this.getStoreDetails();
  }

  get photosFormArray(): FormArray {
    return this.storeSettingsForm.get("photos") as FormArray;
  }

  getStoreDetails(): void {
    this.isLoading = true;

    this.apiService
      .request<any>(
        API_ENDPOINTS.owner.accountSettings.store.getStoreDetails,
        "GET",
      )
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          if (response.data) {
            this.lastLoadedData = response.data;
            this.populateForm(response.data);
          }
        },
        error: (error) => {
          console.error("Error fetching store details", error);
          this.isLoading = false;
        },
      });
  }

  populateForm(data: any): void {
    const store = data.store || data;

    this.storeSettingsForm.patchValue({
      business_name: store.business_name || "",
      userName: store.userName || store.username || "",
      description: store.description || "",
      logo_in_repost: !!store.logo_in_repost,
      logo_in_emails: !!store.logo_in_emails,
      logo: data.logo || store.logo || "",
    });

    this.logoPreview = data.logo || store.logo || null;

    this.photoPreviews = [];
    this.photosFormArray.clear();

    const photos = data.photos || store.photos || [];

    if (Array.isArray(photos)) {
      photos.slice(0, this.MAX_PHOTOS).forEach((photo: any) => {
        const url =
          typeof photo === "string" ? photo : photo?.url || photo?.image || "";

        if (!url) return;

        this.photoPreviews.push(url);
        this.photosFormArray.push(this.fb.control(photo));
      });
    }
  }

  triggerLogoUpload(): void {
    this.logoInput.nativeElement.click();
  }

  triggerPhotosUpload(): void {
    this.photosInput.nativeElement.click();
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files[0]) return;

    const file = input.files[0];

    if (!this.validateFile(file)) return;

    const reader = new FileReader();

    reader.onload = () => {
      this.logoPreview = reader.result as string;
      this.storeSettingsForm.get("logo")?.setValue(reader.result);
      this.storeSettingsForm.get("logo")?.markAsDirty();
    };

    reader.readAsDataURL(file);
  }

  onPhotosSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files) return;

    let files = Array.from(input.files);
    const remainingSlots = this.MAX_PHOTOS - this.photosFormArray.length;

    if (remainingSlots <= 0) {
      this.showWarning(
        `You can upload a maximum of ${this.MAX_PHOTOS} photos.`,
      );
      return;
    }

    if (files.length > remainingSlots) {
      files = files.slice(0, remainingSlots);
      this.showWarning(`Only ${remainingSlots} more photo(s) can be added.`);
    }

    files.forEach((file) => {
      if (!this.validateFile(file)) return;

      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result as string;

        this.photoPreviews.push(result);
        this.photosFormArray.push(this.fb.control(result));
        this.storeSettingsForm.get("photos")?.markAsDirty();
      };

      reader.readAsDataURL(file);
    });

    input.value = "";
  }

  removePhoto(index: number): void {
    this.photoPreviews.splice(index, 1);
    this.photosFormArray.removeAt(index);
    this.storeSettingsForm.get("photos")?.markAsDirty();
  }

  removeLogo(): void {
    this.logoPreview = null;
    this.storeSettingsForm.get("logo")?.setValue("");
    this.storeSettingsForm.get("logo")?.markAsDirty();
  }

  handleSave(): void {
    if (this.storeSettingsForm.invalid) {
      this.storeSettingsForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.apiService
      .request(
        API_ENDPOINTS.owner.accountSettings.store.update,
        "POST",
        this.storeSettingsForm.value,
      )
      .subscribe({
        next: () => {
          this.isLoading = false;

          Swal.fire({
            icon: "success",
            title: "Store settings saved successfully",
            toast: true,
            position: "top-end",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
            customClass: {
              container: "!z-[999999]",
            },
          });

          this.getStoreDetails();
        },
        error: (error) => {
          console.error("Error saving settings", error);
          this.isLoading = false;

          Swal.fire({
            icon: "error",
            title: "Failed to save store settings",
            toast: true,
            position: "top-end",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
            customClass: {
              container: "!z-[999999]",
            },
          });
        },
      });
  }

  resetForm(): void {
    if (this.lastLoadedData) {
      this.populateForm(this.lastLoadedData);
      this.storeSettingsForm.markAsPristine();
      this.storeSettingsForm.markAsUntouched();
      return;
    }

    this.storeSettingsForm.reset({
      business_name: "",
      userName: "",
      description: "",
      logo: "",
      logo_in_repost: false,
      logo_in_emails: false,
    });

    this.logoPreview = null;
    this.photoPreviews = [];
    this.photosFormArray.clear();
  }

  isInvalid(path: string): boolean {
    const control = this.storeSettingsForm.get(path);
    return !!(control?.invalid && (control.touched || control.dirty));
  }

  private validateFile(file: File): boolean {
    if (file.size > this.MAX_FILE_SIZE_MB * 1024 * 1024) {
      this.showWarning(
        `File size should not exceed ${this.MAX_FILE_SIZE_MB}MB.`,
      );
      return false;
    }

    if (!file.type.startsWith("image/")) {
      this.showWarning("Only image files are supported.");
      return false;
    }

    return true;
  }

  private showWarning(message: string): void {
    Swal.fire({
      icon: "warning",
      title: message,
      toast: true,
      position: "top-end",
      timer: 3000,
      timerProgressBar: true,
      showConfirmButton: false,
      customClass: {
        container: "!z-[999999]",
      },
    });
  }
}
